import { useMemo } from 'react';
import useVersionManagement from './useVersionManagement';
import { parseMarkdownIntoSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage architecture versions
 * Stores and tracks different versions of architecture sections from regenerations
 */
const useArchitectureVersions = (conversation, isStreaming, streamingContent) => {
  // Configuration for architecture version management
  const architectureConfig = {
    // Determine if a message contains architecture content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      // Check for markdown headers and architecture-related keywords
      return content.includes('## ') && (
        content.includes('Architecture') ||
        content.includes('Component') ||
        content.includes('Database') ||
        content.includes('API') ||
        content.includes('System') ||
        content.includes('Design') ||
        content.includes('Schema') ||
        content.includes('Service') ||
        content.includes('Infrastructure') ||
        content.includes('Deployment')
      );
    },

    // Parse architecture sections from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseMarkdownIntoSections(content);
    },

    // Extract version information from a message
    extractVersions: (msg, parsedSections, versionNumber, isPreview = false) => {
      return {
        id: msg._id || msg.id || `${msg.createdAt}-${versionNumber}`,
        timestamp: msg.createdAt || new Date().toISOString(),
        sections: parsedSections,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent
  };

  // Use the generic version management hook
  const versionData = useVersionManagement(architectureConfig);

  // Memoize currentVersion to prevent unnecessary re-renders
  const memoizedCurrentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;

    return {
      ...versionData.currentVersion,
      sections: versionData.currentVersion.parsedContent || versionData.currentVersion.sections
    };
  }, [versionData.currentVersion, versionData.currentVersionIndex]);

  // Map the generic version data to architecture-specific structure
  return {
    versions: versionData.versions,
    currentVersion: memoizedCurrentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useArchitectureVersions;
